import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Formio } from 'formiojs';
import { cloneDeep } from 'lodash';

import AllComponents from 'formiojs/components';
import Components from 'formiojs/components/Components';
import {useCustomTemplates} from './hooks/useCustomTemplates';
import './AutoComplete';
import './LazySelect';
import './FormIo.css';

useCustomTemplates();
Components.setComponents(AllComponents);

interface FormWrapperProps {
  schema: any;
  submission?: any;
  options?: {
    readOnly?: boolean;
    noAlerts?: boolean;
    renderMode?: string;
    [key: string]: any;
  };
  onChange?: (data: any) => void;
  onSubmit?: (data: any) => void;
}

// Wrap with forwardRef to accept a ref from the parent
const FormIo = forwardRef<HTMLDivElement, FormWrapperProps>((props, ref) => {
  const { 
    schema, 
    submission, 
    options = { readOnly: false, noAlerts: false, renderMode: 'HWM' }, 
    onChange, 
    onSubmit 
  } = props;

  // We use a local ref internally but sync it with the forwarded ref
  const localRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<any>(null);

  // This hook ensures that the 'ref' passed from the parent points to our local div
  useImperativeHandle(ref, () => localRef.current as HTMLDivElement);

  useEffect(() => {
    let instance: any;

    const initForm = async () => {
      if (!localRef.current || !schema) return;

      // Initialize the form with provided options
      instance = await Formio.createForm(localRef.current, schema, options);
      instanceRef.current = instance;
      
      if (submission) {
        instance.submission = cloneDeep(submission);
      }

      instance.on('change', (event: any) => {
        if (event.changed) {
          const { instance: componentInstance, value, flags } = event.changed;
          
          // Check if it's a real user change and not a background validation/noValidate flag
          if (componentInstance && !flags?.noValidate) {
            if (onChange) {
              // We pass an object containing the path (where it is in the data) 
              // and the new value
              onChange({
                path: componentInstance.path, // e.g., "personalInformation.firstName"
                value: value,
                isValid: event?.isValid,
                component: componentInstance.component // The full component definition
              });
            }
          }
        }
      });

      instance.on('submit', (submissionData: any) => {
        if (onSubmit) onSubmit(submissionData.data);
      });
    };

    initForm();

    return () => {
      if (instance) instance.destroy(true);
    };
  }, [schema, options.readOnly, options.renderMode]);

  // Sync external submission changes
  useEffect(() => {
    if (instanceRef.current && submission) {
      instanceRef.current.submission = cloneDeep(submission);
    }
  }, [submission]);

  return <div className="formio-wrapper" ref={localRef} />;
});

FormIo.displayName = 'FormIo';

export default FormIo;
